import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2 } from "lucide-react";
import { http, errText } from "@/lib/http";

const fmtWeek = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const Kpi = ({ label, value, prev, testId }) => {
  const diff = value - prev;
  return (
    <div className="tile p-5" data-testid={testId}>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6B877]">{label}</p>
      <p className="mt-2 font-display text-3xl text-[#F7F4EE]" data-testid={`${testId}-value`}>{value}</p>
      <p className={`mt-1 text-xs ${diff > 0 ? "text-[#9FD3A8]" : diff < 0 ? "text-[#E8A08A]" : "text-[#C5BDB3]"}`}>
        {diff === 0 ? "Comme la semaine dernière" : `${diff > 0 ? "+" : ""}${diff} vs semaine dernière`}
      </p>
    </div>
  );
};

const tipStyle = { background: "#1E1A17", border: "1px solid rgba(247,244,238,0.12)", borderRadius: 10, color: "#F7F4EE", fontSize: 12 };

export const Stats = () => {
  const [weeks, setWeeks] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    http.get("/admin/stats", { params: { weeks: 8 } })
      .then((r) => setWeeks(Array.isArray(r.data?.weeks) ? r.data.weeks : []))
      .catch((e) => setError(errText(e)));
  }, []);

  if (error) return <p className="text-sm text-[#E8A08A]">{error}</p>;
  if (!weeks) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[#E69C24]" />;

  const cur = weeks[weeks.length - 1] || {};
  const prev = weeks[weeks.length - 2] || {};
  const data = weeks.map((w) => ({ ...w, label: fmtWeek(w.week_start) }));
  const total = (k) => weeks.reduce((n, w) => n + (w[k] || 0), 0);

  return (
    <div className="space-y-6" data-testid="stats-panel">
      <p className="text-sm text-[#C5BDB3]">Cette semaine (depuis lundi). Statistiques anonymes : aucune adresse IP ni donnée personnelle n'est enregistrée, et vos propres visites sur cet appareil ne sont pas comptées.</p>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Visiteurs" value={cur.visitors || 0} prev={prev.visitors || 0} testId="kpi-visitors" />
        <Kpi label="Visites" value={cur.visits || 0} prev={prev.visits || 0} testId="kpi-visits" />
        <Kpi label="CV téléchargés" value={cur.cv_downloads || 0} prev={prev.cv_downloads || 0} testId="kpi-cv" />
        <Kpi label="Messages reçus" value={cur.messages || 0} prev={prev.messages || 0} testId="kpi-messages" />
      </div>
      <div className="tile p-5">
        <h3 className="mb-4 font-display text-lg text-[#F7F4EE]">Les 8 dernières semaines</h3>
        <div className="h-72" data-testid="stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ left: -20, right: 8 }}>
              <CartesianGrid stroke="rgba(247,244,238,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#C5BDB3", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#C5BDB3", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: "rgba(247,244,238,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#C5BDB3" }} />
              <Bar dataKey="visitors" name="Visiteurs" fill="#E69C24" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cv_downloads" name="CV téléchargés" fill="#B84A2E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="tile p-5"><p className="text-xs text-[#C5BDB3]">Entrées dans la chambre 3D (8 sem.)</p><p className="mt-1 font-display text-2xl text-[#F7F4EE]" data-testid="stats-room">{total("room")}</p></div>
        <div className="tile p-5"><p className="text-xs text-[#C5BDB3]">Ouvertures du CV seul (8 sem.)</p><p className="mt-1 font-display text-2xl text-[#F7F4EE]" data-testid="stats-classic">{total("classic")}</p></div>
        <div className="tile p-5"><p className="text-xs text-[#C5BDB3]">Visites en anglais (8 sem.)</p><p className="mt-1 font-display text-2xl text-[#F7F4EE]" data-testid="stats-en">{total("en")}</p></div>
      </div>
    </div>
  );
};
